import { Controller } from 'stimulus'
import { useResize } from 'stimulus-use'
import { chooseStatsShape } from './about_stats_shape.js'

export default class extends Controller {
  static targets = ['hiddenRow', 'hiddenCell']

  connect() {
    // Observes this.element itself (the stats block, not window) — its clientWidth already
    // reflects whatever space flexbox leaves after the authors block, sidebar collapse, modal,
    // orientation change, etc. useResize's initial observe() fires once immediately with the
    // current size, so no separate first-measurement call is needed here.
    useResize(this)

    // The hidden twins are the measurement, so their size is what matters. A late web font
    // (theme fonts load with display=swap) or any other text change widens them without changing
    // this.element's own size, so useResize never fires. Observing the twins re-measures then.
    this.twinObserver = new ResizeObserver(() => this.scheduleMeasure())
    this.hiddenRowTargets.forEach((target) => this.twinObserver.observe(target))
    this.hiddenCellTargets.forEach((target) => this.twinObserver.observe(target))
  }

  disconnect() {
    this.twinObserver?.disconnect()
    this.twinObserver = null
    if (this.measureFrame) {
      cancelAnimationFrame(this.measureFrame)
      this.measureFrame = null
    }
  }

  resize({ width }) {
    this.containerWidth = width
    this.scheduleMeasure()
  }

  scheduleMeasure() {
    if (this.measureFrame) return

    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null
      this.measure()
    })
  }

  // The width the stats would have with the author beside them. The stats block's own width jumps
  // when the author wraps below, which made the shape flip-flop as the viewport grew; the header row
  // minus its other children depends only on the viewport, so the shape does too.
  besideWidth() {
    const row = this.element.parentElement
    if (!row) return this.containerWidth

    const siblings = Array.from(row.children).filter((child) => child !== this.element)
    const siblingsWidth = siblings.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0)
    const rowGap = parseFloat(getComputedStyle(row).columnGap) || 0
    return row.clientWidth - siblingsWidth - (siblings.length ? rowGap : 0)
  }

  measure() {
    if (!this.hasHiddenRowTarget || this.containerWidth == null) return

    const cellCount = this.hiddenCellTargets.length
    if (cellCount === 0) return

    // Both twins are the real markup at natural width: the row is icon + text per stat with the
    // visible row's gap, and each cell is icon + the two stacked lines, as the stacked shape draws it.
    const naturalRowWidth = this.hiddenRowTarget.scrollWidth
    const widestCellNaturalWidth = Math.max(
      ...this.hiddenCellTargets.map((cell) => cell.getBoundingClientRect().width)
    )
    const columnGap = parseFloat(getComputedStyle(this.element).columnGap) || 0

    // The widest single stat in its rows form (icon + gap + full text). The stats block never gets
    // narrower than this (capped at the header row's width), so when an author sits beside the
    // stats and even one stat per row would not fit, the author moves to its own line instead.
    const widestAtomWidth = Math.ceil(
      Math.max(0, ...Array.from(this.hiddenRowTarget.children, (atom) => atom.getBoundingClientRect().width))
    )
    if (this.widestAtomWidth !== widestAtomWidth) {
      this.widestAtomWidth = widestAtomWidth
      this.element.style.setProperty('--about-stats-min', `${widestAtomWidth}px`)
    }

    const shape = chooseStatsShape({
      containerWidth: this.besideWidth(),
      naturalRowWidth,
      widestCellNaturalWidth,
      cellCount,
      columnGap,
    })

    if (this.element.dataset.shape !== shape) {
      this.element.dataset.shape = shape
    }
  }
}
